import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { agentConfigApi } from '../services/api-services';
import { useTestProfile } from '../hooks/useTestProfile';

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  openrouter: 'OpenRouter',
  acp_kiro: 'AcpKiro',
  acp_copilot: 'AcpCopilot',
  acp_claude: 'AcpClaude',
};

interface Props {
  open: boolean;
  employeeId?: number;
  allowedProviders?: string[] | null;
  onClose: () => void;
  onApplied?: (profile: string, model: string) => void;
}

const ModelConfigModal: React.FC<Props> = ({ open, employeeId, allowedProviders, onClose, onApplied }) => {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [activeProfile, setActiveProfile] = useState('');
  const [selected, setSelected] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const { testProfile, testing } = useTestProfile();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const empReq = employeeId
      ? agentConfigApi.getEmployeeConfig(employeeId)
      : Promise.resolve({ profile: '', model: '' });
    Promise.all([agentConfigApi.getGlobalConfig(), empReq])
      .then(([globalRes, empRes]: any) => {
        const profs = globalRes.profiles || [];
        setProfiles(profs);
        setActiveProfile(globalRes.active_profile || '');
        const appliedProfile = empRes.profile || globalRes.active_profile || '';
        setSelected(appliedProfile);
        const active = profs.find((p: any) => p.name === appliedProfile);
        setSelectedModel(empRes.model || active?.default_model || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, employeeId]);

  const handleApply = async () => {
    setApplying(true);
    try {
      if (employeeId) {
        await agentConfigApi.applyProfileToEmployee(employeeId, selected, selectedModel);
      } else {
        await agentConfigApi.applyGlobalProfile(selected);
      }
      message.success('模型配置已应用');
      onApplied?.(selected, selectedModel);
      onClose();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '应用失败');
    } finally {
      setApplying(false);
    }
  };

  const filteredProfiles = allowedProviders
    ? profiles.filter((p: any) => allowedProviders.includes(p.provider))
    : profiles.filter((p: any) => !['acp_kiro', 'acp_copilot'].includes(p.provider));
  const profile = filteredProfiles.find((p: any) => p.name === selected);
  const modelOptions: string[] = profile?.models?.length ? profile.models : (profile?.default_model ? [profile.default_model] : []);
  const canApply = !!selected;

  return (
    <Modal
      open={open}
      title="模型配置"
      onCancel={onClose}
      footer={[
        <Button key="test" icon={<ExperimentOutlined />} loading={testing} disabled={!selected} onClick={() => testProfile(selected)}>测试</Button>,
        <Button key="apply" type="primary" loading={applying} disabled={!canApply} onClick={handleApply}>应用</Button>,
      ]}
      width={480}
    >
      {loading ? <Spin /> : (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
            <select
              value={selected}
              onChange={(e) => {
                setSelected(e.target.value);
                const p = filteredProfiles.find((x: any) => x.name === e.target.value);
                setSelectedModel(p?.default_model || '');
              }}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 14, border: '1px solid var(--ini-border, #d9d9d9)' }}
            >
              <option value="">请选择配置</option>
              {filteredProfiles.map((p: any) => (
                <option key={p.name} value={p.name}>{p.name}{p.name === activeProfile ? ' (默认)' : ''}</option>
              ))}
            </select>
          </div>
          {profile ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {[['Provider', PROVIDER_LABELS[profile.provider] || profile.provider]].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid var(--ini-border, #f0f0f0)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--apple-text-secondary)', width: 100 }}>{label}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{value || '-'}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: '8px 12px', color: 'var(--apple-text-secondary)', width: 100 }}>模型</td>
                  <td style={{ padding: '8px 12px' }}>
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      style={{ padding: '6px 10px', borderRadius: 6, fontSize: 13, border: '1px solid var(--ini-border, #d9d9d9)', fontFamily: 'monospace', width: '100%' }}
                    >
                      {modelOptions.map((m: string) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : filteredProfiles.length === 0 ? (
            <div style={{ color: 'var(--apple-text-secondary)' }}>暂无配置，请先在「大模型配置」页面创建</div>
          ) : null}
        </>
      )}
    </Modal>
  );
};

export default ModelConfigModal;
